import { useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useApproveSubmission,
  useEquipmentModels,
  useMergeSubmission,
  useRejectSubmission,
  useSubmission,
} from '../../hooks/useEquipment';
import { useEquipmentPermissions } from '../../hooks/useEquipmentPermissions';
import type { EquipmentKind, SubmissionPhotoRole } from '../../types';
import { CreateModelForm } from '../tabs/EquipmentOverviewTab';
import { SearchableSelectField, Section } from '../fields';

const ROLE_LABELS: Record<SubmissionPhotoRole, string> = {
  plate: 'Serial plate',
  unit: 'Whole unit',
  cable_entry: 'Cable entry',
  probe: 'Probe placement',
};

const CONFIRMATION = 'These photos show no people, customer names or serial numbers';

interface SubmissionPanelProps {
  submissionId: string;
  onClose: () => void;
  onReviewed?: () => void;
}

/**
 * Review one technician submission. Photos stay private unless ticked for
 * publishing, which requires the confirmation; the serial plate is never offered.
 */
export function SubmissionPanel({ submissionId, onClose, onReviewed }: SubmissionPanelProps) {
  const { canReview } = useEquipmentPermissions();
  const { data: detail, isLoading } = useSubmission(submissionId);
  const { data: library = [] } = useEquipmentModels('active');
  const approve = useApproveSubmission();
  const merge = useMergeSubmission();
  const reject = useRejectSubmission();

  const [published, setPublished] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [draft, setDraft] = useState<{ brand: string; model: string; label: string; kind: EquipmentKind | null; labelTouched: boolean } | null>(null);
  const [reason, setReason] = useState('');

  const submission = detail?.submission;
  const candidates = useMemo(() => detail?.candidates ?? [], [detail]);
  const mergeOptions = useMemo(() => {
    const seen = new Set(candidates.map((c) => c.modelId));
    return [
      ...candidates.map((c) => ({ value: c.modelId, label: `${c.label} (suggested)` })),
      ...library.filter((m) => !seen.has(m.id)).map((m) => ({ value: m.id, label: m.label })),
    ];
  }, [candidates, library]);
  const targetId = mergeTarget ?? candidates[0]?.modelId ?? null;
  const targetLabel = candidates.find((c) => c.modelId === targetId)?.label ?? library.find((m) => m.id === targetId)?.label;

  if (isLoading || !detail || !submission) {
    return (
      <div className="h-full flex items-center justify-center">
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : <p className="text-sm text-muted-foreground">Submission not found.</p>}
      </div>
    );
  }

  const photosBlocked = published.length > 0 && !confirmed;
  const review = { publishPhotoIds: published, photosConfirmed: published.length > 0 && confirmed };
  const done = { onSuccess: () => onReviewed?.() };
  const togglePhoto = (id: string) => setPublished((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  const startApprove = () => {
    setApproving(true);
    setDraft({
      brand: submission.brand,
      model: submission.model,
      label: `${submission.brand} ${submission.model}`,
      kind: submission.kind,
      labelTouched: false,
    });
  };
  const draftValid = !!draft && !!draft.brand.trim() && !!draft.model.trim() && !!draft.label.trim();

  return (
    <div className="h-full flex flex-col border-l bg-background overflow-hidden" data-testid="submission-panel">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b shrink-0">
        <span className="text-sm font-semibold truncate">{submission.brand} {submission.model}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Close panel" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Serial number</dt><dd>{submission.serialNumber ?? '—'}</dd>
          <dt className="text-muted-foreground">Submitted by</dt><dd>{submission.submittedBy}</dd>
          <dt className="text-muted-foreground">Job · site</dt><dd>{[submission.jobNumber, submission.siteName].filter(Boolean).join(' · ') || '—'}</dd>
        </dl>

        {candidates.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {candidates.map((c) => (
              <div key={c.modelId}>Similar model already in the library: {c.label} ({c.sensorCount} sensors)</div>
            ))}
          </div>
        )}

        <Section label="Photos">
          <div className="grid grid-cols-2 gap-3">
            {detail.photos.map((photo) => {
              const label = ROLE_LABELS[photo.role];
              return (
                <figure key={photo.id} className="space-y-1">
                  <img src={photo.url} alt={label} className="h-32 w-full rounded object-cover bg-muted" />
                  <figcaption className="flex items-center gap-2 text-xs">
                    {canReview && photo.role !== 'plate' && (
                      <Checkbox id={`publish-${photo.id}`} aria-label={`Publish ${label.toLowerCase()} photo`}
                        checked={published.includes(photo.id)} onCheckedChange={() => togglePhoto(photo.id)} />
                    )}
                    <span>{label}{photo.role === 'plate' ? ' · never published' : ''}</span>
                  </figcaption>
                </figure>
              );
            })}
          </div>
          {canReview && (
            <div className="flex items-start gap-2 pt-1">
              <Checkbox id="photos-confirmed" aria-label={CONFIRMATION} checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} />
              <Label htmlFor="photos-confirmed" className="text-xs font-normal leading-snug">{CONFIRMATION}</Label>
            </div>
          )}
        </Section>

        {canReview && (
          <>
            <Section label="Merge into an existing model">
              <SearchableSelectField<string> id="merge-target" label="Model" value={targetId} options={mergeOptions}
                placeholder="Choose a model" searchPlaceholder="Search brand or model" emptyText="No model found"
                onChange={(value) => setMergeTarget(value)} />
              <Button size="sm" variant="outline" disabled={!targetId || photosBlocked || merge.isPending}
                onClick={() => targetId && merge.mutate({ id: submission.id, modelId: targetId, ...review }, done)}>
                {targetLabel ? `Merge into ${targetLabel}` : 'Merge'}
              </Button>
            </Section>

            <Section label="New model">
              {!approving ? (
                <Button size="sm" variant="outline" onClick={startApprove}>Approve as new model</Button>
              ) : draft && (
                <div className="space-y-3">
                  <CreateModelForm value={draft} onChange={setDraft} />
                  <Button size="sm" disabled={!draftValid || photosBlocked || approve.isPending}
                    onClick={() => approve.mutate({
                      id: submission.id,
                      model: { brand: draft.brand.trim(), model: draft.model.trim(), label: draft.label.trim(), kind: draft.kind },
                      ...review,
                    }, done)}>
                    Approve
                  </Button>
                </div>
              )}
            </Section>

            <Section label="Reject">
              <Textarea aria-label="Reason for rejecting" placeholder="e.g. Photos are too blurry to identify the unit"
                value={reason} onChange={(e) => setReason(e.target.value)} className="text-sm min-h-[60px]" />
              <Button size="sm" variant="destructive" disabled={!reason.trim() || reject.isPending}
                onClick={() => reject.mutate({ id: submission.id, reason: reason.trim() }, done)}>
                Reject
              </Button>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
